import { PaymentMethod } from "../../../../common/enums";
import { isUSA, redactAccountNumber } from "../../../../common/utils";
import { type DetailedSepaPaymentPrefObjectType } from "../../common/types";
import { type SelectedBankCountry } from "../../Context/types";
import { getIdFromCountry, getNameFromCountry, mapCountryToValueSafely } from "../../mappers";
import { AccountDetailsInputFields } from "./AccountDetails/constants";
import { BankDetailsInputFields } from "./BankDetails/constants";
import { type SepaPaymentPrefInputType } from "./constants";

const stripAndUpperIban = (v?: string | null): string =>
  v?.replace(/\s/g, "").toUpperCase() ?? "";

export const normalizeSwiftCode = (v?: string | null): string =>
  v?.replace(/\s/g, "").toUpperCase() ?? "";

export const mapFormStateToContextState = (
  values: SepaPaymentPrefInputType,
  bankCountry?: SelectedBankCountry,
): DetailedSepaPaymentPrefObjectType => {
  const accountHolderCountryValue = values[AccountDetailsInputFields.AddressCountry];
  const hasValidAccountHolderCountry =
    !!accountHolderCountryValue && accountHolderCountryValue.includes(":");
  const accountHolderCountryName = hasValidAccountHolderCountry
    ? getNameFromCountry(accountHolderCountryValue)
    : "";
  const accountHolderCountryId = hasValidAccountHolderCountry
    ? getIdFromCountry(accountHolderCountryValue)
    : "";
  const isUsAccountHolderAddress = isUSA(accountHolderCountryName);

  // Prefer the resolved bankCountry argument (which carries alpha2code from the country query).
  // Fall back to parsing the BankCountry form value so the mapper remains usable in unit tests
  // and when the upstream context hydration has not completed.
  const bankCountryFormValue = values[BankDetailsInputFields.BankCountry];
  const hasValidBankCountryFormValue =
    !!bankCountryFormValue && bankCountryFormValue.includes(":");
  const resolvedBankCountryId =
    bankCountry?.id ??
    (hasValidBankCountryFormValue ? getIdFromCountry(bankCountryFormValue) : "");
  const resolvedBankCountryName =
    bankCountry?.name ??
    (hasValidBankCountryFormValue ? getNameFromCountry(bankCountryFormValue) : "");
  const resolvedBankCountryAlpha2 = bankCountry?.alpha2code ?? null;
  const rawIban = values[AccountDetailsInputFields.Iban];
  const iban = stripAndUpperIban(rawIban);

  return {
    __typename: "SepaPaymentPrefObjectType",
    id: null,
    paymentPrefId: null,
    paymentMethod: PaymentMethod.SEPA,
    bank: {
      __typename: "SepaBankObjectType",
      name: values[BankDetailsInputFields.BankName]?.trim() || null,
      bicSwiftCode: normalizeSwiftCode(values[BankDetailsInputFields.BicSwiftCode]) || null,
      address: {
        __typename: "PaymentPrefAddressObjectType",
        street: null,
        street2: null,
        street3: null,
        city: null,
        region: null,
        state: null,
        postalCode: null,
        country: resolvedBankCountryId
          ? {
              __typename: "CountryObjectType",
              id: resolvedBankCountryId,
              name: resolvedBankCountryName,
              alpha2code: resolvedBankCountryAlpha2,
              code: null,
            }
          : null,
      },
    },
    iban: iban || null,
    accountName: values[AccountDetailsInputFields.AccountName]?.trim() || null,
    additionalInstructions:
      values[AccountDetailsInputFields.AdditionalInstructions]?.trim() || null,
    accountHolderAddress: {
      __typename: "PaymentPrefAddressObjectType",
      street: values[AccountDetailsInputFields.AddressStreetLine1]?.trim() || null,
      street2: values[AccountDetailsInputFields.AddressStreetLine2]?.trim() || null,
      street3: null,
      city: values[AccountDetailsInputFields.AddressCity]?.trim() || null,
      region: !isUsAccountHolderAddress
        ? values[AccountDetailsInputFields.AddressProvince]?.trim() || null
        : null,
      state: isUsAccountHolderAddress
        ? {
            __typename: "StateObjectType",
            code: values[AccountDetailsInputFields.AddressState]?.trim() ?? "",
            id: values[AccountDetailsInputFields.AddressState]?.trim() ?? "",
            name: "",
          }
        : null,
      postalCode: values[AccountDetailsInputFields.AddressZip]?.trim() || null,
      country: accountHolderCountryId
        ? {
            __typename: "CountryObjectType",
            id: accountHolderCountryId,
            name: accountHolderCountryName,
            alpha2code: null,
            code: null,
          }
        : null,
    },
    bankInfoRedacted: `${
      values[BankDetailsInputFields.BankName]?.trim() || "Bank"
    } IBAN ending in -${iban ? redactAccountNumber(iban) : ""}`,
  };
};

export const mapContextStateToFormState = (
  input?: DetailedSepaPaymentPrefObjectType,
  lockedBankCountry?: SelectedBankCountry,
): SepaPaymentPrefInputType => {
  const storedIban = input?.iban ?? "";
  const accountHolderCountryName = input?.accountHolderAddress?.country?.name ?? "";
  const isUsAccountHolderAddress = isUSA(accountHolderCountryName);
  // Initial bank country form value: prefer the existing pref's bank country, then any country
  // already locked in upstream (selectedBankCountry from context). When neither is present the
  // dropdown is empty and the user must pick a SEPA-enabled country on the form.
  const existingBankCountry = input?.bank?.address?.country;
  const initialBankCountry = existingBankCountry?.id
    ? mapCountryToValueSafely(existingBankCountry.id, existingBankCountry.name ?? undefined)
    : lockedBankCountry?.id
      ? mapCountryToValueSafely(lockedBankCountry.id, lockedBankCountry.name)
      : "";
  return {
    [BankDetailsInputFields.BankName]: input?.bank?.name ?? "",
    [BankDetailsInputFields.BankCountry]: initialBankCountry,
    [BankDetailsInputFields.BankCountryValidatedCountry]: "",
    [BankDetailsInputFields.BicSwiftCode]: input?.bank?.bicSwiftCode ?? "",
    [AccountDetailsInputFields.AccountName]: input?.accountName ?? "",
    [AccountDetailsInputFields.Iban]: storedIban,
    [AccountDetailsInputFields.ConfirmIban]: storedIban,
    [AccountDetailsInputFields.AdditionalInstructions]: input?.additionalInstructions ?? "",
    [AccountDetailsInputFields.AddressStreetLine1]: input?.accountHolderAddress?.street ?? "",
    [AccountDetailsInputFields.AddressStreetLine2]: input?.accountHolderAddress?.street2 ?? "",
    [AccountDetailsInputFields.AddressCity]: input?.accountHolderAddress?.city ?? "",
    [AccountDetailsInputFields.AddressState]: isUsAccountHolderAddress
      ? input?.accountHolderAddress?.state?.code ?? ""
      : "",
    [AccountDetailsInputFields.AddressProvince]: !isUsAccountHolderAddress
      ? input?.accountHolderAddress?.region ?? ""
      : "",
    [AccountDetailsInputFields.AddressZip]: input?.accountHolderAddress?.postalCode ?? "",
    [AccountDetailsInputFields.AddressCountry]: mapCountryToValueSafely(
      input?.accountHolderAddress?.country?.id ?? undefined,
      input?.accountHolderAddress?.country?.name ?? undefined,
    ),
  };
};
